import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { clerkPlugin } from '@clerk/vue'
import PrimeVue from 'primevue/config'
import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'

// Custom slate/gray theme like PrimeNG demo
const SlatePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{slate.50}',
      100: '{slate.100}',
      200: '{slate.200}',
      300: '{slate.300}',
      400: '{slate.400}',
      500: '{slate.500}',
      600: '{slate.600}',
      700: '{slate.700}',
      800: '{slate.800}',
      900: '{slate.900}',
      950: '{slate.950}',
    },
  },
})
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'

import App from './App.vue'
import router from './router'
import { initPosthog } from './lib/posthog'
import './style.css'
import './primevue-dark-fix.css' // generated — see scripts/generate-primevue-dark-fix.mjs
import 'primeicons/primeicons.css'

initPosthog()

const app = createApp(App)

// Pinia state management
const pinia = createPinia()
app.use(pinia)

// TanStack Query for data fetching
app.use(VueQueryPlugin, {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: 1, // keep — Render cold starts are real
        // Alt-tabbing back refetched every mounted query at once, which is what turned a
        // quiet Sunday into gigabytes of egress. Freshness comes from the agent-events
        // stream and each query's own interval instead. The trade: after a long alt-tab,
        // non-pushed data can be up to staleTime old until a mount, interval or SSE frame.
        // The notification bell opts back in explicitly (useNotifications) — its two
        // responses are tiny and focus-refetch is the reconcile net for missed frames.
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        // Route revisits used to come back cold and refetch after 5 min away.
        gcTime: 30 * 60 * 1000,
      },
    },
  },
})

// Clerk authentication
app.use(clerkPlugin, {
  publishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
  appearance: {
    elements: {
      // Hide the "Delete account" block on the Security tab of Clerk's account
      // modal. Reps were finding it during training (Angel's 2026-09 feedback).
      // The real switch is the Clerk Dashboard setting "Allow users to delete
      // their accounts" (user_settings.actions.delete_self), turned OFF on both
      // instances; this is the belt-and-suspenders UI hide in case it's ever
      // re-enabled by accident.
      profileSection__danger: { display: 'none' },
      // The account popover has no height cap of its own. With several
      // signed-in sessions (or a short window / big zoom) it grew past the
      // viewport and its top ran off-screen with no way to scroll to it.
      // Cap the card to the space above the sidebar trigger and let the main
      // section scroll; the "Sign out of all accounts" footer stays pinned.
      userButtonPopoverCard: { maxHeight: 'calc(100vh - 9rem)' },
      userButtonPopoverMain: { overflowY: 'auto', minHeight: 0 },
    },
  },
})

// PrimeVue UI components with slate theme
app.use(PrimeVue, {
  theme: {
    preset: SlatePreset,
    options: {
      darkModeSelector: '.dark-mode',
    },
  },
})
app.use(ToastService)
app.use(ConfirmationService)

app.use(router)

app.mount('#app')
